import { configureStore } from '@reduxjs/toolkit';
import listenerMiddleware from './middlewares/listenerMiddleware';
import prefsReducer from './prefs';
import { fireSubscriberCallbacks, register } from './subscribers/stateChangeSubscriber';
import projectStateReducer from './projectState';
import jobsStateReducer from './jobs';

const reducer = {
    prefs: prefsReducer,
    projectState: projectStateReducer,
    jobs: jobsStateReducer,
};

const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        serializableCheck: {
            // removes error message for non-serializable data in redux
            ignoredActionPaths: ['payload.mapCase', 'payload.mapCases', 'payload.compounds'],
            ignoredPaths: ['projectState.targetList', 'projectState.scoringTable.rows'],
        },
    }).concat(listenerMiddleware),
});

// Add store subscribers
// The previous implementation of firing subscriber callbacks relied on a hack with createSelector.
// As the memoization behavior in that package changed, we need to find another way. Hopefully there
// is a better way than tracking the previous state here, but this lets us finish the upgrade.
// This PR concerns the previous hack: https://github.com/Conifer-Point/bmaps/pull/242/files
//
// Start from the REAL initial state. It used to start from an empty one, which made the page's
// first dispatch look like a change of every registered key: subscribers that had run by then
// got one free call with the initial value, later ones got nothing, and whichever code
// dispatched first decided which. Start-up values are now applied explicitly by their owners
// (display_mgr.applyCurrentColorScheme, ui/init_info_display), and a subscriber means "on
// change" for everyone.
let previousState = store.getState();
store.subscribe(() => {
    const currentState = store.getState();
    fireSubscriberCallbacks(previousState, currentState);
    previousState = currentState;
});

register('prefs.ColorScheme', (state) => state.prefs.ColorScheme);
register('prefs.BindingSiteDistance', (state) => state.prefs.BindingSiteDistance);
register('prefs.PiPiAlgorithm', (state) => state.prefs.PiPiAlgorithm);
register('prefs.UseMockServiceWorker', (state) => state.prefs.UseMockServiceWorker);
// Force-field-switching prefs: subscribed by display_mgr.js to clear the typed-parameters
// cache, whose entries embed FF-specific terms that must not survive a FF change (step 5.x).
register('prefs.MinimizationEngine', (state) => state.prefs.MinimizationEngine);
register('prefs.Forcefield', (state) => state.prefs.Forcefield);
register('prefs.Visualizer', (state) => state.prefs.Visualizer);
register('prefs.CartoonStyle', (state) => state.prefs.CartoonStyle);
register('prefs.ColorStyle', (state) => state.prefs.ColorStyle);
register('jobs.jobs', (state) => state.jobs.jobs);

/**
 * @typedef {ReturnType<typeof store.getState>} RootState
 * @typedef {typeof store.dispatch} AppDispatch
 */

export default store;
